import React, { useMemo } from 'react';
import { FixedSizeGrid as Grid } from 'react-window';
import clsx from 'clsx';
import { useWindowSize } from '@hooks/useWindowSize';
import { calculateGridColumns, calculateItemWidth } from '@utils/helpers';
import { PHOTO_GRID_SETTINGS } from '@utils/constants';
import PhotoCard from './PhotoCard';
import LoadingSpinner from '@components/common/LoadingSpinner';
import type { Photo, PhotoGridProps } from '@/types';

const PhotoGrid: React.FC<PhotoGridProps> = ({
  photos,
  loading = false,
  onPhotoSelect,
  selectable = true,
  className,
}) => {
  const { width } = useWindowSize();

  // Calculate grid dimensions
  const { columnCount, itemWidth, itemHeight } = useMemo(() => {
    const containerWidth = Math.max(width - 64, 300); // Account for padding with minimum width

    const cols = calculateGridColumns(
      containerWidth,
      PHOTO_GRID_SETTINGS.MIN_COLUMN_WIDTH,
      PHOTO_GRID_SETTINGS.ITEM_GAP
    );

    const itemW = calculateItemWidth(containerWidth, cols, PHOTO_GRID_SETTINGS.ITEM_GAP);

    const itemH = itemW + PHOTO_GRID_SETTINGS.ITEM_HEIGHT_OFFSET; // Add height for photo info

    return {
      columnCount: cols,
      itemWidth: itemW,
      itemHeight: itemH,
    };
  }, [width]);

  const handlePhotoView = (photo: Photo) => {
    onPhotoSelect(photo);
  };

  // Grid item renderer
  const GridItem = ({
    columnIndex,
    rowIndex,
    style,
  }: {
    columnIndex: number;
    rowIndex: number;
    style: React.CSSProperties;
  }) => {
    const index = rowIndex * columnCount + columnIndex;
    const photo = photos[index];

    if (!photo) return null;

    return (
      <div
        style={{
          ...style,
          padding: PHOTO_GRID_SETTINGS.ITEM_GAP / 2,
        }}
      >
        <PhotoCard
          photo={photo}
          onView={handlePhotoView}
          selectable={selectable}
          className="h-full"
        />
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <LoadingSpinner size="large" />
          <p className="text-gray-500 mt-2">Loading photos...</p>
        </div>
      </div>
    );
  }

  if (!photos || photos.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="max-w-md mx-auto">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No photos found</h3>
          <p className="text-gray-500">
            Try adjusting your search criteria or check back later for new photos.
          </p>
        </div>
      </div>
    );
  }

  const rowCount = Math.ceil(photos.length / columnCount);
  const gridHeight = Math.min(
    window.innerHeight - 200, // Leave space for header/footer
    rowCount * (itemHeight + PHOTO_GRID_SETTINGS.ITEM_GAP)
  );

  return (
    <div className={clsx('w-full', className)}>
      {/* Grid info */}
      <div className="flex items-center justify-between mb-4 text-sm text-gray-500">
        <span>
          {photos.length} photo{photos.length !== 1 ? 's' : ''}
        </span>
        <span>
          {columnCount} column{columnCount !== 1 ? 's' : ''} × {rowCount} row
          {rowCount !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Virtualized grid */}
      <div className="overflow-hidden rounded-lg">
        <Grid
          columnCount={columnCount}
          columnWidth={itemWidth + PHOTO_GRID_SETTINGS.ITEM_GAP}
          height={gridHeight}
          rowCount={rowCount}
          rowHeight={itemHeight + PHOTO_GRID_SETTINGS.ITEM_GAP}
          width={Math.max(width - 64, 300)}
          overscanRowCount={PHOTO_GRID_SETTINGS.OVERSCAN_ROW_COUNT}
          overscanColumnCount={PHOTO_GRID_SETTINGS.OVERSCAN_COLUMN_COUNT}
          className="scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent"
        >
          {GridItem}
        </Grid>
      </div>

      {/* Load more indicator */}
      {photos.length > 0 && photos.length % 50 === 0 && (
        <div className="flex justify-center py-8">
          <p className="text-gray-500 text-sm">Scroll down to load more photos</p>
        </div>
      )}
    </div>
  );
};

export default PhotoGrid;
